import type {
  BillingPlan,
  StripeInvoice,
  SubscriptionData,
} from 'api/subscription/subscription.types';
import type { AddOnNames, BoxFeature, Features } from 'types/account';
import { actionCreatorFactory } from 'typescript-fsa';
import type { UserPermissionsFacade } from './user.types';

const actionCreator = actionCreatorFactory('timely/subscriptions');

export type ModalMessageKeyTypes =
  | 'projectLimitReached'
  | 'userLimitReached'
  | 'featureTeamsRequiresUpgrade'
  | 'featureAuditRequiresUpgrade'
  | 'subscriptionExpired'
  | 'trialEnded'
  | 'featureHourlyRatesRequiresUpgrade'
  | 'featureAnomaliesRequiresUpgrade'
  | 'featureTagRequiresUpgrade'
  | 'featureRequiredNotesRequiresUpgrade'
  | 'featureBudgetRequiresUpgrade'
  | 'featureIntercomSupportRequiresUpgrade' // NOTE: not the same as prioritized support
  | 'featureCompanyViewRequiresUpgrade'
  | 'featureBilledHoursRequiresUpgrade'
  | 'featureProjectNotificationsRequiresUpgrade'
  | 'featureLoggedCostRequiresUpgrade'
  | 'featureProfitRequiresUpgrade'
  | 'featureProfitabilityRequiresUpgrade'
  | 'teamsLimitReached'
  | 'featureLiveReportsRequiresUpgrade'
  | 'featureIndividualCapacityRequiresUpdate'
  | 'premiumIntegrationsRequiresUpgrade'
  | 'azureADIntegrationRequiresUpgrade'
  | 'featureTeamLeadRequiresUpgrade'
  | 'featureTasksRequiresUpgrade'
  | 'featureHighLevelReportsRequiresUpgrade'
  | 'featureAccountBrandingRequiresUpgrade'
  | 'featureHourStatesRequiresUpgrade';

export type InlineMessageKeyTypes =
  | 'memoryRequiresUpgrade'
  | 'logForOthersRequiresUpgrade'
  | 'usersDashboardRequiresUpgrade'
  | 'projectsDashboardRequiresUpgrade'
  | 'ticIntegrationsRequiresUpgrade';

export type SubscriptionMessageKeyTypes = ModalMessageKeyTypes | InlineMessageKeyTypes;

/** The React subscription settings page (`routes/subscription`). */
export interface SubscriptionPageState {
  subscription: SubscriptionData | null;
  invoices: Array<StripeInvoice>;
  /** Current plans for both cycles; null while loading or when they failed to load. */
  plans: Array<BillingPlan> | null;
  plansFailed: boolean;
  isLoading: boolean;
  loadError: string | null;
  /** The latest load; responses from an older one are ignored. */
  loadId: number;
}

export interface State {
  showMessageKey?: ModalMessageKeyTypes;
  addOnScopeKey?: AddOnNames;
  page: SubscriptionPageState;
}

/**
 * Use @see showSubscriptionNoticeModal in stead
 */
export const setSubscriptionMessageKey = actionCreator<ModalMessageKeyTypes>('SET_MESSAGE_KEY');
export const clearMessageKey = actionCreator('CLEAR_MESSAGE_KEY');

export const setAddOnScopeKey = actionCreator<AddOnNames>('SET_ADD_ON_SCOPE_KEY');

export const pageLoadStarted = actionCreator<{ loadId: number; showSpinner: boolean }>(
  'PAGE_LOAD_STARTED'
);
export const pageLoaded = actionCreator<{
  loadId: number;
  subscription: SubscriptionData;
  invoices?: Array<StripeInvoice>;
}>('PAGE_LOADED');
export const pageLoadFailed = actionCreator<{ loadId: number; message: string }>(
  'PAGE_LOAD_FAILED'
);
export const plansLoaded = actionCreator<Array<BillingPlan>>('PLANS_LOADED');
export const plansLoadFailed = actionCreator('PLANS_LOAD_FAILED');

export interface SubscriptionInfo {
  isEnterprise: boolean;
  isAdmin: boolean;
  isTeam: boolean;
  isInTrial: boolean;
  isPaid: boolean;
  accountOwnerEmail?: string;
  planName?: string;
  accountId: number;
  subscriptionMessageKey?: ModalMessageKeyTypes;
  memoryHistoryDays: number;
  features: Features;
}

export interface UpgradePermissionsInfo {
  canUserUpgradeSubscription: boolean;
  canContactWorkspaceOwner: boolean;
  canContactAccountManager: boolean;
  shouldOpenPlansModal: boolean;
  shouldOpenExternalSalesPage?: boolean;
  accountOwnerEmail?: string;
  accountId: number;
}

export interface SubscriptionNoticeModalProps {
  subscriptionInfo: SubscriptionInfo;
  userPermissions: UserPermissionsFacade;
  messageKey?: ModalMessageKeyTypes;
  featuresByPlan?: Record<string, Array<BoxFeature>> | null;
}

export const FEATURE_OR_LIMIT_NAME = 'featureOrLimitName';
export const SENDER_NAME = 'senderName';
export const OWNER_NAME = 'ownerName';
export const OWNER_EMAIL = 'ownerMail';
export const MEMORY_LIMIT = 'memoryLimit';

type ContentRenderFunc = (subscriptionInfo: SubscriptionInfo) => string | JSX.Element;

export type TextContent = string | JSX.Element | ContentRenderFunc;

export interface PerTeamTextGroup {
  default?: TextContent;
  team?: TextContent;
  individual?: TextContent;
}

export interface PerEnterpriseTextGroup {
  default?: TextContent;
  enterprise?: PerTeamTextGroup;
  nonEnterprise?: PerTeamTextGroup;
}

export interface PerUserTypeTextGroup {
  default: TextContent;
  admin?: PerEnterpriseTextGroup;
  limited?: PerTeamTextGroup;
  normal?: TextContent;
}

export type AdaptiveCustomerText = PerUserTypeTextGroup | TextContent;

export interface SubscriptionMessage {
  trackingKey: string;
  title?: AdaptiveCustomerText;
  description?: AdaptiveCustomerText;
  isDismissable: boolean;
  upgradeText?: TextContent;
  cancelText?: AdaptiveCustomerText;

  featureOrLimitName: string;

  contactAccountManagerTemplate: {
    subject: string;
    body: string;
  };

  contactWorkspaceAdminTemplate: {
    subject: string;
    body: string;
  };
}

export interface TalkBeforeCancelForm {
  name: string;
  email: string;
  phoneNo: string | number;
  comment: string;
  timezone: string;
  giftcardValueUSD: number;
}
